import { DateOnly } from '@if/core/date'
import { formatUtcIsoDateTime } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { ControlledDateOnlyPicker, type ErrorOverrides, useFormDialog } from '@if/mui'
import { Stack } from '@mui/material'
import { useUpdateScheduledCustomBuydownPlan } from '@src/data/api/custom-buydown-api/custom-buydown-api'
import { CustomBuydownPlan } from '@src/types/CustomBuydown'
import { Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getPromoPlanErrorCode, PromoPlanErrorCode } from '../promoPlanErrorCodes'
import { buildWindowOverlapMessage } from '../windowEditErrors'

const rescheduleSchema = () =>
  yup.object({
    activeFrom: yup.dateOnly().required().min(DateOnly.today()),
    expiresOn: yup.dateOnly().defined().nullable().min(yup.ref('activeFrom'), { offsetDays: 1 }),
  })

type RescheduleFormValues = yup.InferSubmit<ReturnType<typeof rescheduleSchema>>

export const useRescheduleUpcomingPlanDialog = (merchantId: string, activePlan?: CustomBuydownPlan | null) => {
  const { t } = useTranslation()
  const updateSchedulePlan = useUpdateScheduledCustomBuydownPlan(merchantId)

  const onError: ErrorOverrides = {
    notFound: (_descriptor, t) => ({ role: 'contextual', message: t('customBuydown.errors.windowEditPlanNotFound') }),
    unknown: (descriptor, t) => {
      const code = getPromoPlanErrorCode(descriptor)
      if (code === PromoPlanErrorCode.ActivationDateInPast) {
        return { role: 'contextual', message: t('customBuydown.errors.activationDateInPast') }
      }
      if (code === PromoPlanErrorCode.EndDateInPast) {
        return { role: 'contextual', message: t('customBuydown.errors.endDateInPast') }
      }
      if (code === PromoPlanErrorCode.PlanAlreadyEnded) {
        return { role: 'contextual', message: t('customBuydown.errors.windowEditPlanAlreadyEnded') }
      }
      if (code === PromoPlanErrorCode.ActivationDateFrozen) {
        return { role: 'contextual', message: t('customBuydown.errors.activationDateFrozen') }
      }
      return undefined
    },
    conflict: (descriptor, t) => buildWindowOverlapMessage(descriptor, activePlan, t),
  }

  return useFormDialog<RescheduleFormValues, CustomBuydownPlan, CustomBuydownPlan>({
    icon: Calendar,
    title: (ctx) => t('customBuydown.timeline.changeDatesTitle', { planCode: ctx.props.label }),
    schema: rescheduleSchema(),
    defaultValues: (plan) => ({
      activeFrom: new DateOnly(plan.activeFrom),
      expiresOn: plan.expiresOn ? new DateOnly(plan.expiresOn) : null,
    }),
    onError,
    content: (ctx) => (
      <Stack direction="row" spacing={2}>
        <ControlledDateOnlyPicker
          control={ctx.form.control}
          name="activeFrom"
          label={t('customBuydown.timeline.starts')}
          fullWidth
        />
        <ControlledDateOnlyPicker
          control={ctx.form.control}
          name="expiresOn"
          label={t('customBuydown.timeline.ends')}
          clearable
          fullWidth
        />
      </Stack>
    ),
    successMessage: (saved) => t('customBuydown.timeline.editDatesSaved', { planCode: saved.label }),
    onSubmit: async (data, ctx) =>
      updateSchedulePlan.mutateAsync({
        promoPlanId: ctx.props.id,
        dto: {
          activeFrom: formatUtcIsoDateTime(data.activeFrom.toDate()),
          expiresOn: data.expiresOn ? formatUtcIsoDateTime(data.expiresOn.toDate()) : null,
          versionTag: ctx.props.versionTag,
        },
      }),
  })
}
